import { createStore, applyMiddleware, compose } from 'redux';
import { offline } from '@redux-offline/redux-offline';
import defaultConfig from '@redux-offline/redux-offline/lib/defaults';
import Promise from 'bluebird';
import { find } from 'lodash/fp';
import gql from 'graphql-tag';

import offlineUndoEnhancer from './utils/offline-undo-enhancer';
import bugsnagClient from './clients/bugsnag-client';
import lysterClient from './clients/lyster-client';
import apolloClient from './clients/apollo-client';
import { initializationComplete } from './modules/system/actions';
import { isConnectedSelector } from './modules/system/selectors';
import { isLoggedInSelector, tokenSelector, userIdSelector } from './modules/auth/selectors';
import { undoValidUntilSelector } from './modules/todo/selectors';
import setupWithStore from './setup-with-store';
import { updateUserInfo } from './utils/analytics';
import middleware from './store-middleware';
import pusher from './utils/pusher';
import defaultReducers from './modules';
import storage from './storage';
import waitForUndo from './observables/waitForUndo';


const Mutations = {
  startMorningsAtMinuteOffset: gql`mutation UpdateStartMorningsAtMinuteOffset(
    $token:ID!,
    $startMorningsAtMinuteOffset:Int!,
    $timeOfChange:DateTime!
  ){
    auth(token:$token)
    startMorningsAtMinuteOffset: updateStartMorningsAtMinuteOffset(
      startMorningsAtMinuteOffset:$startMorningsAtMinuteOffset,
      timeOfChange:$timeOfChange
    )
  }
`,
  startEveningsAtMinuteOffset: gql`mutation UpdateStartEveningsAtMinuteOffset(
    $token:ID!,
    $startEveningsAtMinuteOffset:Int!,
    $timeOfChange:DateTime!
  ){
    auth(token:$token)
    startEveningsAtMinuteOffset: updateStartEveningsAtMinuteOffset(
      startEveningsAtMinuteOffset:$startEveningsAtMinuteOffset,
      timeOfChange:$timeOfChange
    )
  }
`,
  laterTodayDelayMinutes: gql`
  mutation UpdateLaterTodayDelayMinutes(
    $token:ID!,
    $laterTodayDelayMinutes:Int!,
    $timeOfChange:DateTime!
  ){
    auth(token:$token)
    laterTodayDelayMinutes: updateLaterTodayDelayMinutes(
      laterTodayDelayMinutes:$laterTodayDelayMinutes,
      timeOfChange:$timeOfChange
    )
  }`,
  lastPostponedDate: gql`
  mutation UpdateLastPostponedDate(
    $token:ID!,
    $lastPostponedDate:GraphQLDateTimeLuxon!,
    $timeOfChange:DateTime!
  ){
    auth(token:$token)
    lastPostponedDate: updateLastPostponedDate(
      lastPostponedDate:$lastPostponedDate,
      timeOfChange:$timeOfChange
    )
  }`,
};


class UndoError extends Error {
  constructor(...args) {
    super(...args);

    Error.captureStackTrace && Error.captureStackTrace(this, UndoError);
    this.name = 'UndoError';
  }
}

function storeCreator(reducers = defaultReducers, additionalMiddleware = []) {
  let store, //eslint-disable-line
      previousUserId,
      channel,
      onlineStatus;

  const deltaExistsSelector = (state, id, changeId) => {
    const { outbox } = state.offline;
    return (
      find((a) => a.payload.changeId === changeId, outbox)
    );
  };

  const offlineConfig = {
    ...defaultConfig,
    persistOptions: {
      blacklist: ['system', 'network', 'nav', 'routing'],
      storage,
    },
    persistCallback: () => {
      const token = tokenSelector(store.getState());
      updateUserInfo(token);
      store.dispatch(initializationComplete());
    },
    detectNetwork: (callback) => Promise.delay(0).then(() => store.subscribe(() => {
      const state = store.getState(),
            isConnected = isConnectedSelector(state),
            isLoggedIn = isLoggedInSelector(state),
            userId = userIdSelector(state),
            status = isConnected && isLoggedIn;

      if (previousUserId !== userId) {
        if (channel) {
          channel.unsubscribe(`user_${previousUserId}`);
        }
        if (userId) {
          channel = pusher.subscribe(`user_${userId}`);

          const syncAction = (payload) => {
            store.dispatch({ type: 'TODO_SYNC', payload });
          };
          channel.bind('todo_updated', syncAction);
          channel.bind('todo_added', syncAction);
        }
        previousUserId = userId;
      }

      if (onlineStatus !== status) {
        onlineStatus = status;
        bugsnagClient.leaveBreadcrumb(`Online status = ${status}`, {
          isConnected,
          isLoggedIn,
        });
        return Promise.delay(0).then(() => callback(status));
      }
      return undefined;
    })),
    effect: async (effect, { payload }) => {
      if (effect.type === 'MUTATION') {
        const { mutationName, options } = effect,
              mutation = Mutations[mutationName];

        const token = tokenSelector(store.getState()),
              optionsWithToken = { ...options, variables: { ...options.variables, token } };

        return apolloClient.mutate({ mutation, ...optionsWithToken });
      }

      const { id, changeId } = payload,
            diff = undoValidUntilSelector(store.getState()) - new Date();

      if (diff > 0) {
        waitForUndo.set(true);
        setTimeout(() => {
          waitForUndo.set(false);
        }, diff);
      } else {
        waitForUndo.set(false);
      }

      await waitForUndo.waitFor(false);

      const state = store.getState();

      if (!await deltaExistsSelector(state, id, changeId)) {
        throw new UndoError('Action has been undone!');
      }

      return lysterClient(effect);
    },
    discard: (error) => {
      if (error.graphQLErrors) {
        const userInputError = error.graphQLErrors.find((e) => e.extensions.code === 'BAD_USER_INPUT');

        if (userInputError) {
          alert(userInputError.message);
        }

        return !!userInputError;
      }
      if (error.message === 'Action has been undone!') {
        return false; // already removed
      } if (error.message === 'Network Error') {
        return false;
      } if (error.response && error.response.status) {
        const { status } = error.response;
        if (status >= 400 && status <= 499) {
          return true;
        }
        return false;
      }
      return false; // should only occur if you cannot reach server
    },
    retry: (action, previousAttempts) => {
      if (previousAttempts === 0) {
        return 1000;
      }
      if (previousAttempts <= 2) {
        return 10 * 1000;
      }
      if (previousAttempts <= 4) {
        return 60 * 1000;
      }
      if (previousAttempts <= 6) {
        return 2 * 60 * 1000;
      }
      if (previousAttempts <= 8) {
        return 5 * 60 * 1000;
      }
      if (previousAttempts <= 10) {
        return 10 * 60 * 1000;
      }
      if (previousAttempts <= 12) {
        return 15 * 60 * 1000;
      }
      if (previousAttempts <= 20) {
        return 30 * 60 * 1000;
      }
      if (previousAttempts <= 30) {
        return 60 * 60 * 1000;
      }
      return 24 * 60 * 60 * 1000;
    },
  };

  if (__DEV__) {
    // eslint-disable-next-line import/no-extraneous-dependencies, global-require
    const logger = require('redux-logger').createLogger({
      duration: true,
    }); //eslint-disable-line
    middleware.push(logger);
    // eslint-disable-next-line global-require
    const Reactotron = require('./reactotron');

    store = createStore(
      reducers,
      compose(
        applyMiddleware(...[...middleware, ...additionalMiddleware]),
        offline(offlineConfig),
        offlineUndoEnhancer,
        Reactotron.createEnhancer(),
      ),
    );
  } else {
    store = createStore(
      reducers,
      compose(
        applyMiddleware(...[...middleware, ...additionalMiddleware]),
        offline(offlineConfig),
        offlineUndoEnhancer,
      ),
    );
  }
  setupWithStore(store);
  return store;
}

export default storeCreator;
